import EditIcon from '@mui/icons-material/Edit';
import { FC, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import API from 'api/API';
import Theme from 'App.theme';
import ViewEnlargedImageModal from 'components/ViewEnlargedImageModal/ViewEnlargedImageModal';
import { useLanguage } from 'context/AppContext';
import { getSrc, StorageType } from 'utils/helpers/image.helpers';
import * as Styles from './AsyncImage.styles';

interface AsyncImageProps {
  src?: string;
  setCode?: string;
  trayRef?: string;
  alt: string;
  updateTrigger?: number;
  openAnnotateImage?: () => void;
  isEnlargedImageAvailable?: boolean;
  imgHeight?: number | string;
  imgWidth?: number | string;
  storageType: StorageType;
  editable?: boolean;
}

const AsyncImage: FC<AsyncImageProps> = ({
  src,
  setCode,
  trayRef,
  alt,
  imgWidth,
  storageType,
  imgHeight,
  updateTrigger,
  openAnnotateImage,
  isEnlargedImageAvailable,
  editable,
  ...rest
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFail, setIsFail] = useState(false);
  const [isEnlargedImageModalOpen, setIsEnlargedImageModalOpen] = useState(false);
  const [language] = useLanguage();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (src || (setCode && trayRef)) {
        try {
          const resolvedSrc = await getSrc(storageType, src, setCode, trayRef);
          resolvedSrc && setResolvedSrc(resolvedSrc);
          setIsFail(!resolvedSrc);
        } catch (error) {
          API.errorHandler(error);
        }
      }
      setIsLoading(false);
    })();
  }, [setCode, src, storageType, trayRef, updateTrigger]);

  if (isLoading)
    return (
      <ContentLoader
        speed={2}
        width={imgWidth}
        height={imgHeight}
        backgroundColor={Theme.greys.GREY0}
        foregroundColor={Theme.greys.GREY1}
        {...rest}
      >
        <rect x="0" y="0" rx="10" ry="10" width="100%" height="100%" />
      </ContentLoader>
    );

  if (isFail)
    return (
      <Styles.AsyncImageContainer imgHeight={imgHeight} imgWidth={imgWidth} {...rest}>
        <Styles.AsyncImageFailContainer>{language.AsyncImage.fail}</Styles.AsyncImageFailContainer>
      </Styles.AsyncImageContainer>
    );

  if (!resolvedSrc)
    return (
      <Styles.AsyncImageContainer imgHeight={imgHeight} imgWidth={imgWidth} {...rest}>
        <Styles.AsyncImageFailContainer>{language.AsyncImage.none}</Styles.AsyncImageFailContainer>
      </Styles.AsyncImageContainer>
    );

  return (
    <Styles.AsyncImageContainer
      imgHeight={imgHeight}
      imgWidth={imgWidth}
      isEnlargedImageAvailable={isEnlargedImageAvailable}
      {...rest}
    >
      <Styles.AsyncImageStyle onClick={() => setIsEnlargedImageModalOpen(true)} alt={alt} src={resolvedSrc} />
      {editable && (
        <Styles.EditButtonStyle color="primary" onClick={openAnnotateImage}>
          <EditIcon />
        </Styles.EditButtonStyle>
      )}
      {isEnlargedImageAvailable ? (
        <ViewEnlargedImageModal
          modalOpen={isEnlargedImageModalOpen}
          setClosed={() => setIsEnlargedImageModalOpen(false)}
          alt={alt}
          src={resolvedSrc}
        />
      ) : null}
    </Styles.AsyncImageContainer>
  );
};

export default AsyncImage;
